import TomSelect from "tom-select";
import BInput from "../../modules/BInput";
import BRequest from "../../modules/BRequest";

/**
 * Build rich option HTML for TomSelect (header, title, footer, image).
 * Item may have: value, text, header, title, footer, image (from data-* or API).
 */
function renderRichOption(item, escape)
{
	const title = escape(item.title ?? item.text ?? "");
	const header = item.header ? escape(item.header) : "";
	const footer = item.footer ? escape(item.footer) : "";
	const image = item.image ? escape(item.image) : "";
	const imageBlock = image
		? `<div class="ts-option-rich__image-wrap"><img class="ts-option-rich__image" src="${image}" alt="" loading="lazy"></div>`
		: "";
	return `<div class="ts-option-rich">
		${imageBlock}
		<div class="ts-option-rich__content">
			${header ? `<span class="ts-option-rich__header">${header}</span>` : ""}
			<span class="ts-option-rich__title">${title}</span>
			${footer ? `<span class="ts-option-rich__footer">${footer}</span>` : ""}
		</div>
	</div>`;
}

/**
 * Build rich item (selected pill) HTML.
 */
function renderRichItem(item, escape)
{
	const title = escape(item.title ?? item.text ?? "");
	const image = item.image ? escape(item.image) : "";
	if (image) {
		return `<span class="ts-option-rich-item"><img class="ts-option-rich-item__image" src="${image}" alt="">${title}</span>`;
	}
	return `<span>${title}</span>`;
}

/**
 * Read options from a <select> and enrich with data-header, data-title, data-footer, data-image.
 */
function getRichOptionsFromSelect(selectEl)
{
	const options = [];
	const selected = [];
	for (const opt of selectEl.options) {
		const value = opt.value;
		const text = (opt.textContent || opt.innerText || "").trim();
		options.push({
			value,
			text,
			title: opt.dataset.title ?? text,
			header: opt.dataset.header ?? "",
			footer: opt.dataset.footer ?? "",
			image: opt.dataset.image ?? "",
		});
		if (opt.selected) {
			selected.push(value);
		}
	}
	return { options, selected };
}

export default class BSelect
{
	static selector = () => `.b-select`;

	static initialize()
	{
		document.querySelectorAll(this.selector()).forEach(el => {
			if (el.closest(".prototype")) return;
			// Skip selects inside a closed dialog – they are initialized when the dialog opens (see BDialogTrigger).
			const dialog = el.closest("dialog");
			if (dialog && !dialog.open) return;
			this.initializeElement(el);
		});
	}

	static initializeElement(el)
	{
		if (el.tomselect) return;

		const isRich = el.classList.contains("b-select--rich");
		// Inside a dialog: omit dropdownParent so the dropdown stays in the TomSelect wrapper (already inside the dialog's top layer).
		// Outside: append to body to avoid overflow clipping from ancestor elements.
		const dialog = el.closest("dialog");

		let options = {
			...(dialog ? {} : { dropdownParent: "body" }),
			onFocus: () => {
				const bInput = el.closest(`.b-input`);
				if (bInput) new BInput(bInput).setFocused();
			},
			onBlur: () => {
				const bInput = el.closest(`.b-input`);
				if (bInput) new BInput(bInput).unsetFocused();
			},
			render: {
				loading: (data, escape) => `<div class="no-results">Načítám&hellip;</div>`,
				no_results: (data, escape) => `<div class="no-results">Nic nenalezeno.</div>`,
			},
		};

		if (isRich) {
			const { options: richOptions, selected } = getRichOptionsFromSelect(el);
			el.innerHTML = "";
			const valueSet = new Set(richOptions.map((o) => String(o.value)));
			const validItems = selected.filter((v) => valueSet.has(String(v)));
			richOptions.forEach((o) => {
				const opt = document.createElement("option");
				opt.value = String(o.value);
				opt.textContent = o.text;
				if (o.header) opt.dataset.header = o.header;
				if (o.footer) opt.dataset.footer = o.footer;
				if (o.image) opt.dataset.image = o.image;
				if (validItems.includes(String(o.value))) opt.selected = true;
				el.appendChild(opt);
			});
			options = {
				...options,
				options: richOptions,
				valueField: "value",
				labelField: "text",
				items: validItems,
				render: {
					...options.render,
					option: renderRichOption,
					item: renderRichItem,
				},
			};
		}

		if (el.multiple) {
			options.maxItems = null;
			options.closeAfterSelect = true;
		} else {
			options.maxItems = 1;
			options.closeAfterSelect = true;
		}

		if (el.dataset?.remoteDatasource) {
			options.load = (query, callback) => {
				BRequest.get(el.dataset.remoteDatasource, {
					query: query,
				}).then(response => {
					let items = response.data;
					items.map(item => {
						if (!item?.text) {
							item.text = item?.label;
						}
					});
					callback(items);
				});
			}
			options.delimiter = ";;;";
		}

		if ("allowCreate" in (el.dataset ?? {})) {
			options.create = true;
		}

		return new TomSelect(el, options);
	}
}
